import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import type { CalendarEvent, CalendarEventType } from "../types";

const TYPE_DEFS: {
  value: CalendarEventType;
  label: string;
  color: string;
}[] = [
  { value: "meeting", label: "Meetings", color: "#3B82F6" },
  { value: "task", label: "Tasks", color: "#8B5CF6" },
  { value: "action", label: "Actions", color: "#06B6D4" },
  { value: "milestone", label: "Milestones", color: "#10B981" },
];

interface CalendarFiltersProps {
  enabledTypes: Set<CalendarEventType>;
  onToggleType: (type: CalendarEventType) => void;
  assignedToMe: boolean;
  onAssignedToMeChange: (v: boolean) => void;
  companyId: string | null;
  onCompanyChange: (id: string | null) => void;
  projectId: string | null;
  onProjectChange: (id: string | null) => void;
  events: CalendarEvent[];
}

export function CalendarFilters({
  enabledTypes,
  onToggleType,
  assignedToMe,
  onAssignedToMeChange,
  companyId,
  onCompanyChange,
  projectId,
  onProjectChange,
  events,
}: CalendarFiltersProps) {
  const companies = useCompanyList({
    limit: 100,
    tab: "operational",
    operationalOnly: true,
  });
  const projects = useProjectList({
    limit: 100,
    companyId: companyId ?? undefined,
  });

  const counts: Record<CalendarEventType, number> = {
    meeting: 0,
    task: 0,
    action: 0,
    milestone: 0,
  };
  for (const ev of events) counts[ev.type]++;

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Show
        </div>
        <div className="space-y-1">
          {TYPE_DEFS.map((t) => {
            const checked = enabledTypes.has(t.value);
            return (
              <label
                key={t.value}
                className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-[hsl(210,50%,96%)]"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleType(t.value)}
                  className="h-3.5 w-3.5 cursor-pointer rounded border-[hsl(210,30%,75%)]"
                  style={
                    checked
                      ? {
                          accentColor: t.color,
                        }
                      : undefined
                  }
                />
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: t.color }}
                />
                <span className="flex-1 text-[13px] text-[hsl(215,16%,30%)]">
                  {t.label}
                </span>
                <span className="rounded-full bg-[hsl(210,30%,93%)] px-1.5 text-[10px] font-medium text-[hsl(215,16%,40%)]">
                  {counts[t.value]}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="border-t pt-3" style={{ borderColor: "hsl(210,30%,90%)" }}>
        <label className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-1.5 py-1 hover:bg-[hsl(210,50%,96%)]">
          <span className="text-[13px] text-[hsl(215,16%,30%)]">
            My items only
          </span>
          <input
            type="checkbox"
            checked={assignedToMe}
            onChange={(e) => onAssignedToMeChange(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer rounded border-[hsl(210,30%,75%)]"
          />
        </label>
      </div>

      <div className="border-t pt-3" style={{ borderColor: "hsl(210,30%,90%)" }}>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Filter by
        </div>
        <div className="space-y-2">
          <Select
            className="h-8 text-[12px]"
            value={companyId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              onCompanyChange(v ? v : null);
              onProjectChange(null);
            }}
          >
            <option value="">All companies</option>
            {(companies.data?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select
            className="h-8 text-[12px]"
            value={projectId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              onProjectChange(v ? v : null);
            }}
          >
            <option value="">All projects</option>
            {(projects.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
}
